/**
 * リアルタイム対戦用 React hook（T15）。
 *
 * 責務:
 *   - マウント時: ws-ticket 取得 → WebSocket 接続 → 対戦開始
 *   - 受信: signal メッセージを dispatch し、盤面/手番/勝敗を同期する
 *   - 送信: placeMove / forfeit を WebSocket 経由で broadcast
 *   - 状態変化: connectionStatus / battleSnapshot / moveTimeLeft / errorMessage
 *   - アンマウント: disconnect（leaveRoom は呼ばない。画面側で明示的に退出させる）
 *
 * 設計:
 *   - サーバー権威 reducer は未使用（本ゲームでは登録していない）
 *   - 両クライアントで engine.ts の純粋関数を走らせる決定論同期
 *   - 受信した PlaceStone が自分の着手であれば無視（自分の UI は即時反映済み）
 *   - ホスト（black）が StartMatch を送り、両者の BattleSnapshot を同時に初期化
 *
 * エラーは catch で握らず、errorMessage / connectionStatus='error' で UI に伝播する
 * （no-silent-fallback.md ルール遵守）。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  REALTIME_COMMAND_TYPES,
  REALTIME_MESSAGE_TYPES,
  WS_MOVE_TIMEOUT_MS,
  type BattleMember,
  type BattleResult,
  type BattleSnapshot,
  type RealtimeCommandEnvelope,
  type RealtimeConnectionState,
} from '../config/realtime'
import {
  applyForfeit,
  applyPlaceStone,
  buildInitialBattle,
  restoreSnapshot,
  serializeSnapshot,
  type SerializedBattleSnapshot,
} from '../services/battleSync'
import { RealtimeClient } from '../services/realtime'
import type { RoomInfo } from '../config/multiplayer'
import type { LocalProfile } from '../services/hubProfile'

// ---------------------------------------------------------------------------
// オプション / 返り値の型
// ---------------------------------------------------------------------------

export interface UseRealtimeBattleOptions {
  room: RoomInfo
  selfUserId: string
  selfProfile: LocalProfile
  /** Hub API base URL（VITE_YMG_API_BASE_URL）。WS URL 構築と realtime-state 取得に使う。 */
  apiBaseUrl: string
  /** 対戦終了時に呼ばれる。画面側は自動で閉じて結果画面に遷移する。 */
  onGameOver: (result: BattleResult) => void
}

export interface UseRealtimeBattleResult {
  connectionStatus: RealtimeConnectionState
  /** 'reconnecting' 時の試行回数。UI 表示用。 */
  reconnectAttempt: number
  battleSnapshot: BattleSnapshot | null
  /** 現在の手番プレイヤーに残された時間（ms）。手番が変わるたびに WS_MOVE_TIMEOUT_MS にリセット。 */
  moveTimeLeft: number
  errorMessage: string | null
  /** 自分の色（black=先手 / white=後手）。初期化前は null。 */
  selfColor: 'black' | 'white' | null
  placeMove: (row: number, col: number) => void
  forfeit: () => void
  /** 手動再接続要求（error 状態からの復旧用）。 */
  reconnect: () => void
  /** 明示的な退出（タイトルに戻る）。leaveRoom は画面側で別途呼ぶ。 */
  exit: () => void
  /** 結果を発火せずに切断する。エラー画面や開始前キャンセル用。 */
  close: () => void
}

// ---------------------------------------------------------------------------
// hook 本体
// ---------------------------------------------------------------------------

export function useRealtimeBattle(
  options: UseRealtimeBattleOptions,
): UseRealtimeBattleResult {
  const { room, selfUserId, selfProfile, apiBaseUrl, onGameOver } = options

  const [connectionStatus, setConnectionStatus] = useState<RealtimeConnectionState>('idle')
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const [battleSnapshot, setBattleSnapshot] = useState<BattleSnapshot | null>(null)
  const [moveTimeLeft, setMoveTimeLeft] = useState(WS_MOVE_TIMEOUT_MS)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const clientRef = useRef<RealtimeClient | null>(null)
  const snapshotRef = useRef<BattleSnapshot | null>(null)
  const gameOverFiredRef = useRef(false)
  const onGameOverRef = useRef(onGameOver)
  onGameOverRef.current = onGameOver
  // selfProfile / handleSignal は対戦中に親側で変化する可能性がある。
  // doConnect 内で client.on(Signal, handleSignal) として直接バインドしてしまうと、
  // 古いクロージャ（=古い selfProfile）を持った handleSignal が使われ続ける。
  // ref 経由で常に最新を参照することで再接続なしに最新の値を反映する。
  const selfProfileRef = useRef(selfProfile)
  selfProfileRef.current = selfProfile

  // 自分の色はルームのホスト ID から導出する（buildInitialBattle と同じルール）。
  const selfColor = useMemo<'black' | 'white' | null>(() => {
    if (!selfUserId) return null
    return room.hostUserId === selfUserId ? 'black' : 'white'
  }, [room.hostUserId, selfUserId])

  // ---------------------------------------------------------------------------
  // snapshot 更新ヘルパ
  // ---------------------------------------------------------------------------

  const commitSnapshot = useCallback((next: BattleSnapshot) => {
    snapshotRef.current = next
    setBattleSnapshot(next)
    setMoveTimeLeft(WS_MOVE_TIMEOUT_MS)

    if (next.gameOver && !gameOverFiredRef.current) {
      gameOverFiredRef.current = true
      const winnerColor = next.winner
      let playerWon: boolean | null = null
      let reason: BattleResult['reason']
      if (winnerColor === 'draw' || winnerColor === null) {
        reason = 'draw'
        playerWon = null
      } else if (winnerColor === selfColor) {
        reason = 'win'
        playerWon = true
      } else {
        reason = 'lose'
        playerWon = false
      }
      onGameOverRef.current({ reason, playerWon, snapshot: next })
    }
  }, [selfColor])

  // ---------------------------------------------------------------------------
  // メッセージハンドラ
  // ---------------------------------------------------------------------------

  /** signal メッセージ（アプリケーション層のコマンド）を処理する。 */
  const handleSignal = useCallback(
    (fromUserId: string | undefined, envelope: RealtimeCommandEnvelope | undefined) => {
      if (!envelope) return
      const { commandType, payload } = envelope

      switch (commandType) {
        case REALTIME_COMMAND_TYPES.StartMatch: {
          // ホストのみが送る。snapshot を初期化する。
          const existing = snapshotRef.current
          // 既に初期化済みなら members 情報だけ更新する
          if (existing) {
            const p = payload as { opponentProfile?: { userId: string; displayName: string; avatarUrl: string | null } } | undefined
            const opp = p?.opponentProfile
            if (opp) {
              const members = upsertMembers(existing.members, {
                userId: opp.userId,
                color: existing.members.find((m) => m.userId === opp.userId)?.color
                  ?? (opp.userId === room.hostUserId ? 'black' : 'white'),
                displayName: opp.displayName,
                avatarUrl: opp.avatarUrl,
                online: true,
              })
              commitSnapshot({ ...existing, members })
            }
            return
          }
          const p = payload as { hostProfile?: { userId: string; displayName: string; avatarUrl: string | null } } | undefined
          const hostProfile = p?.hostProfile
          const opponentProfile = hostProfile && hostProfile.userId !== selfUserId
            ? hostProfile
            : null
          // 親側で selfProfile が更新されている可能性があるため ref 経由で最新を取る。
          const currentSelfProfile = selfProfileRef.current
          const selfProfileForInit = {
            displayName: currentSelfProfile.displayName,
            avatarUrl: currentSelfProfile.pictureUrl ?? null,
          }
          const snap = buildInitialBattle(room, selfUserId, selfProfileForInit, opponentProfile)
          commitSnapshot(snap)
          return
        }
        case REALTIME_COMMAND_TYPES.PlaceStone: {
          const prev = snapshotRef.current
          if (!prev) return
          const p = payload as { row: number; col: number; color: 'black' | 'white' } | undefined
          if (!p) return
          // 自分の着手の echo は無視する（自分の UI は即時反映済み）
          const placerIsSelf = prev.turn === selfColor && p.color === selfColor
          const remoteMatchesExpectedPlayer =
            (fromUserId === room.hostUserId && p.color === 'black') ||
            (fromUserId && fromUserId !== room.hostUserId && p.color === 'white')
          if (placerIsSelf) return
          // 不正検知: from と color の食い違いは無視
          if (fromUserId && !remoteMatchesExpectedPlayer) {
            console.warn('[useRealtimeBattle] unexpected place_stone from', fromUserId, p)
            return
          }
          const next = applyPlaceStone(prev, p.row, p.col, p.color)
          if (!next) {
            console.warn('[useRealtimeBattle] invalid remote move:', p, prev)
            return
          }
          commitSnapshot(next)
          return
        }
        case REALTIME_COMMAND_TYPES.Forfeit: {
          const prev = snapshotRef.current
          if (!prev || prev.gameOver) return
          const p = payload as { color: 'black' | 'white' } | undefined
          const forfeiter = p?.color ?? (fromUserId === room.hostUserId ? 'black' : 'white')
          const next = applyForfeit(prev, forfeiter)
          const updatedResultReason: BattleResult['reason'] =
            forfeiter === selfColor ? 'forfeit' : 'opponent_forfeit'
          // commitSnapshot は通常の win/lose を発火するので、投了は先に専用イベントを発火する
          if (!gameOverFiredRef.current) {
            gameOverFiredRef.current = true
            snapshotRef.current = next
            setBattleSnapshot(next)
            onGameOverRef.current({
              reason: updatedResultReason,
              playerWon: forfeiter === selfColor ? false : true,
              snapshot: next,
            })
            return
          }
          return
        }
        case REALTIME_COMMAND_TYPES.RequestState: {
          // 相手が再接続直後に送信。host が自分で、かつ snapshot を保持している場合に返す。
          const prev = snapshotRef.current
          if (!prev) return
          if (!fromUserId) return
          const client = clientRef.current
          if (!client) return
          try {
            client.sendTo(fromUserId, REALTIME_COMMAND_TYPES.StateSnapshot, serializeSnapshot(prev))
          } catch (err) {
            console.warn('[useRealtimeBattle] sendTo StateSnapshot failed:', err)
          }
          return
        }
        case REALTIME_COMMAND_TYPES.StateSnapshot: {
          // ホストが authority。ホスト以外から送られた snapshot は無視する。
          // この防御がないと、ルームに参加した任意のクライアントが直接
          // WebSocket で state_snapshot を送ることで盤面を任意上書きできる
          // （cheat / DoS の経路）。
          if (fromUserId !== room.hostUserId) {
            console.warn('[useRealtimeBattle] StateSnapshot from non-host ignored:', fromUserId)
            return
          }
          const serialized = payload as SerializedBattleSnapshot | undefined
          if (!serialized) return
          const prev = snapshotRef.current
          const members = prev?.members ?? []
          commitSnapshot(restoreSnapshot(serialized, members))
          return
        }
        default:
          console.warn('[useRealtimeBattle] unknown commandType:', commandType)
      }
    },
    // selfProfile は selfProfileRef 経由で参照するため deps から除外する
    [commitSnapshot, room, selfColor, selfUserId],
  )

  // ---------------------------------------------------------------------------
  // 接続ライフサイクル
  // ---------------------------------------------------------------------------

  const doConnect = useCallback(async () => {
    gameOverFiredRef.current = false
    snapshotRef.current = null
    setBattleSnapshot(null)
    setErrorMessage(null)
    setReconnectAttempt(0)

    const client = new RealtimeClient({ roomId: room.roomId, apiBaseUrl })
    clientRef.current = client

    client.onStateChange((state, detail) => {
      setConnectionStatus(state)
      if (state === 'reconnecting' && detail?.attempt !== undefined) {
        setReconnectAttempt(detail.attempt)
      }
      if (state === 'error') {
        const err = client.getLastError()
        setErrorMessage(err?.message ?? detail?.reason ?? '接続エラー')
        if (!gameOverFiredRef.current) {
          gameOverFiredRef.current = true
          onGameOverRef.current({
            reason: 'connection_lost',
            playerWon: null,
            snapshot: snapshotRef.current,
          })
        }
      }
    })

    client.on(REALTIME_MESSAGE_TYPES.Signal, (msg) => {
      handleSignal(msg.from, msg.payload as RealtimeCommandEnvelope)
    })

    client.on(REALTIME_MESSAGE_TYPES.PlayerJoined, () => {
      // 新規参加時の処理は snapshot 側で members を同期するが、
      // ホストが先手なので、ホスト=自分なら StartMatch を送信する。
      const prev = snapshotRef.current
      if (prev) return // 既に開始済みなら何もしない
      if (selfUserId !== room.hostUserId) return
      sendStartMatch()
    })

    client.on(REALTIME_MESSAGE_TYPES.PlayerReconnected, () => {
      // 自分がホストで、相手が再接続してきた場合に現在の snapshot を送信する。
      const prev = snapshotRef.current
      if (!prev) return
      if (selfUserId !== room.hostUserId) return
      try {
        client.sendCommand(REALTIME_COMMAND_TYPES.StateSnapshot, serializeSnapshot(prev))
      } catch (err) {
        console.warn('[useRealtimeBattle] snapshot broadcast failed:', err)
      }
    })

    client.on(REALTIME_MESSAGE_TYPES.Connected, (msg) => {
      // 初回接続 or 再接続で受信する。自分が非ホストで既に snapshot 未取得なら host に要求する。
      const p = msg.payload as { hostUserId?: string; members?: Array<{ userId: string; online?: boolean }> } | undefined
      const hostUserId = p?.hostUserId ?? room.hostUserId
      const members = p?.members ?? []

      // 非ホストで snapshot がまだ無い場合、再接続時でもホストから取り寄せる
      if (selfUserId !== hostUserId && !snapshotRef.current) {
        try {
          client.sendTo(hostUserId, REALTIME_COMMAND_TYPES.RequestState, {})
        } catch (err) {
          console.warn('[useRealtimeBattle] RequestState failed:', err)
        }
      }

      // ゲストが先に対戦画面へ入り、後からホストが接続したケースでは
      // PlayerJoined を取り逃がす可能性があるため、Connected 時の members でも開始判定する。
      if (
        selfUserId === hostUserId &&
        !snapshotRef.current &&
        members.some((member) => member.userId !== selfUserId)
      ) {
        sendStartMatch()
      }
    })

    await client.connect()
  }, [apiBaseUrl, handleSignal, room, selfUserId])

  const sendStartMatch = useCallback(() => {
    const client = clientRef.current
    if (!client) return
    // ホスト側で snapshot を初期化してから配信。
    // selfProfile は ref 経由で最新を取る（親で edit 直後でも反映されるように）。
    const currentSelfProfile = selfProfileRef.current
    const selfProfileForInit = {
      displayName: currentSelfProfile.displayName,
      avatarUrl: currentSelfProfile.pictureUrl ?? null,
    }
    const snap = buildInitialBattle(room, selfUserId, selfProfileForInit, null)
    commitSnapshot(snap)

    try {
      client.sendCommand(REALTIME_COMMAND_TYPES.StartMatch, {
        hostProfile: {
          userId: selfUserId,
          displayName: currentSelfProfile.displayName,
          avatarUrl: currentSelfProfile.pictureUrl ?? null,
        },
      })
    } catch (err) {
      console.warn('[useRealtimeBattle] StartMatch failed:', err)
    }
    // selfProfile は selfProfileRef 経由で参照
  }, [commitSnapshot, room, selfUserId])

  // マウント時に接続開始
  useEffect(() => {
    void doConnect().catch((err) => {
      setConnectionStatus('error')
      setErrorMessage(err instanceof Error ? err.message : String(err))
      if (!gameOverFiredRef.current) {
        gameOverFiredRef.current = true
        onGameOverRef.current({
          reason: 'connection_lost',
          playerWon: null,
          snapshot: null,
        })
      }
    })
    return () => {
      clientRef.current?.disconnect()
      clientRef.current = null
    }
    // 接続は room.roomId と selfUserId 変化時のみ張り直す。
    // doConnect / handleSignal は selfProfile 等を ref 経由で参照しており、
    // 参照そのものが変わっても再接続は不要（再接続すると対戦が壊れる）。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.roomId, selfUserId])

  // ---------------------------------------------------------------------------
  // 手番タイマー
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!battleSnapshot || battleSnapshot.gameOver) return
    const startedAt = battleSnapshot.turnStartedAt
    const tick = () => {
      const elapsed = Date.now() - startedAt
      const left = Math.max(0, WS_MOVE_TIMEOUT_MS - elapsed)
      setMoveTimeLeft(left)
    }
    tick()
    const h = setInterval(tick, 250)
    return () => clearInterval(h)
  }, [battleSnapshot])

  // ---------------------------------------------------------------------------
  // 公開アクション
  // ---------------------------------------------------------------------------

  const placeMove = useCallback(
    (row: number, col: number) => {
      const client = clientRef.current
      if (!client) return
      const prev = snapshotRef.current
      if (!prev) return
      if (selfColor === null) return
      if (prev.turn !== selfColor) return
      if (prev.gameOver) return

      const next = applyPlaceStone(prev, row, col, selfColor)
      if (!next) return

      // 勝利確定手（gameOver）は commitSnapshot 内で onGameOver を発火する。
      // sendCommand が失敗したのに onGameOver だけ呼ばれると、相手側は着手を
      // 受け取っていないのに自分の親はリザルトに遷移してしまい、状態が完全に
      // 不整合になる。送信成功を確認してから commit する。
      if (next.gameOver) {
        try {
          client.sendCommand(REALTIME_COMMAND_TYPES.PlaceStone, {
            row,
            col,
            color: selfColor,
          })
        } catch (err) {
          console.warn('[useRealtimeBattle] PlaceStone (gameOver) send failed:', err)
          setErrorMessage('着手の送信に失敗しました')
          return
        }
        commitSnapshot(next)
        return
      }

      // 通常手は即時反映（対称 engine により整合が保たれる）。
      // 送信失敗時は rollback して再着手を促す。
      commitSnapshot(next)
      try {
        client.sendCommand(REALTIME_COMMAND_TYPES.PlaceStone, {
          row,
          col,
          color: selfColor,
        })
      } catch (err) {
        console.warn('[useRealtimeBattle] PlaceStone send failed:', err)
        setErrorMessage('着手の送信に失敗しました')
        snapshotRef.current = prev
        setBattleSnapshot(prev)
      }
    },
    [commitSnapshot, selfColor],
  )

  const forfeit = useCallback(() => {
    const client = clientRef.current
    if (!client) return
    const prev = snapshotRef.current
    if (!prev || prev.gameOver) return
    if (selfColor === null) return

    const next = applyForfeit(prev, selfColor)
    snapshotRef.current = next
    setBattleSnapshot(next)
    if (!gameOverFiredRef.current) {
      gameOverFiredRef.current = true
      onGameOverRef.current({
        reason: 'forfeit',
        playerWon: false,
        snapshot: next,
      })
    }
    try {
      client.sendCommand(REALTIME_COMMAND_TYPES.Forfeit, { color: selfColor })
    } catch (err) {
      console.warn('[useRealtimeBattle] forfeit send failed:', err)
    }
  }, [selfColor])

  const reconnect = useCallback(() => {
    clientRef.current?.disconnect()
    clientRef.current = null
    void doConnect().catch((err) => {
      setConnectionStatus('error')
      setErrorMessage(err instanceof Error ? err.message : String(err))
    })
  }, [doConnect])

  const exit = useCallback(() => {
    clientRef.current?.disconnect()
    clientRef.current = null
    if (!gameOverFiredRef.current) {
      gameOverFiredRef.current = true
      onGameOverRef.current({
        reason: 'exited',
        playerWon: null,
        snapshot: snapshotRef.current,
      })
    }
  }, [])

  const close = useCallback(() => {
    clientRef.current?.disconnect()
    clientRef.current = null
  }, [])

  return {
    connectionStatus,
    reconnectAttempt,
    battleSnapshot,
    moveTimeLeft,
    errorMessage,
    selfColor,
    placeMove,
    forfeit,
    reconnect,
    exit,
    close,
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function upsertMembers(existing: BattleMember[], member: BattleMember): BattleMember[] {
  const idx = existing.findIndex((m) => m.userId === member.userId)
  if (idx === -1) return [...existing, member]
  const next = [...existing]
  next[idx] = { ...next[idx], ...member }
  return next
}
